import React from 'react';
import moment from 'moment-timezone';
import { useQuery } from '@apollo/client/react';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { GET_ME } from '../../graphql/me';
import { GET_CALENDAR_FEED } from '../../graphql/calendar_feed';
import { GET_TRAINER_CHECK_INS } from '../../graphql/trainer_check_in';

const localizer = momentLocalizer(moment);

const StyledCalendar = styled(Calendar)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  minHeight: 500,
}));

function CalendarTab({ workoutSessions, user }) {
  const { error: meError, data: meData } = useQuery(GET_ME);
  const trainerId = meData?.me?._id;
  const { error: calendarFeedError, data: calendarFeedData } = useQuery(
    GET_CALENDAR_FEED,
    {
      variables: {
        trainerId,
        userId: user?._id,
      },
      skip: !trainerId || !user?._id,
    }
  );

  const { error: checkInsError, data: checkInsData } = useQuery(
    GET_TRAINER_CHECK_INS,
    {
      variables: {
        input: {
          trainerId,
          userId: user?._id,
          status: 'SCHEDULED',
          limit: 100,
        },
      },
      skip: !trainerId || !user?._id,
    }
  );
  const handleSelectEvent = (event) => {
    if (event.resourceType === 'CHECK_IN') {
      return;
    }

    window.location.assign(`/trainer/workout_sessions/${event._id}`);
  };

  const workoutEvents = workoutSessions.map((workoutSession) => {
    const endedAt = workoutSession.completedAt || workoutSession.createdAt;
    const event = { ...workoutSession };
    event.start = new Date(endedAt);
    event.end = new Date(endedAt);
    event.resourceType = 'WORKOUT_SESSION';
    return event;
  });
  const checkInEvents = (checkInsData?.trainerCheckIns || [])
    .filter((checkIn) => checkIn.scheduledStartAt && checkIn.scheduledEndAt)
    .map((checkIn) => ({
      ...checkIn,
      title: `Check-in: ${user?.name || 'Client'}`,
      start: new Date(checkIn.scheduledStartAt),
      end: new Date(checkIn.scheduledEndAt),
      resourceType: 'CHECK_IN',
    }));
  const events = [...workoutEvents, ...checkInEvents];

  const latestEvent = events[events.length - 1];
  const calendarFeedUrl = calendarFeedData?.calendarFeed?.url;
  const handleCopyCalendarFeed = () => {
    if (!calendarFeedUrl) {
      return;
    }
    navigator.clipboard.writeText(calendarFeedUrl);
  };

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={12} md={10}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Client calendar feed
              </Typography>
              <Typography color="textSecondary" variant="body2" sx={{ mb: 2 }}>
                Share this private feed URL with the client so scheduled
                check-ins appear on their calendar. Calendar apps refresh
                subscribed feeds on their own schedule, so updates may not
                appear immediately.
              </Typography>
              {calendarFeedError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Calendar feed URL could not be loaded.
                </Alert>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} mb={3}>
                <TextField
                  fullWidth
                  label="Private calendar feed URL"
                  value={calendarFeedUrl || ''}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  disabled={!calendarFeedUrl}
                  onClick={handleCopyCalendarFeed}
                  variant="outlined"
                >
                  Copy
                </Button>
                <Button
                  component={Link}
                  disabled={!calendarFeedUrl}
                  href={calendarFeedUrl || '#'}
                  target="_blank"
                  underline="none"
                  variant="outlined"
                >
                  Open
                </Button>
              </Stack>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Apple Calendar
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    iPhone or iPad: copy the feed URL, open Settings, go to
                    Calendar, Accounts, Add Account, Other, Add Subscribed
                    Calendar, paste the URL, then tap Next and Save.
                  </Typography>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                    sx={{ mt: 1 }}
                  >
                    Mac: copy the feed URL, open Calendar, choose File, New
                    Calendar Subscription, paste the URL, then subscribe.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Google Calendar
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Open Google Calendar on desktop/web, use Other calendars,
                    From URL, paste the feed URL, then add the calendar.
                  </Typography>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                    sx={{ mt: 1 }}
                  >
                    Google Calendar mobile apps usually cannot add subscribed
                    calendars directly. Add it on web first, then enable the
                    calendar in the mobile app.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              {(meError || checkInsError) && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  Scheduled trainer calendar events could not be loaded.
                </Typography>
              )}
              <StyledCalendar
                selectable
                localizer={localizer}
                events={events}
                defaultView="month"
                scrollToTime={latestEvent?.end || new Date()}
                defaultDate={new Date()}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={() => {}}
                tooltipAccessor={(event) =>
                  event.resourceType === 'CHECK_IN'
                    ? event.meetingUrl || 'Scheduled check-in'
                    : event.title
                }
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default CalendarTab;
